import { useState, useMemo } from "react";
import { useTours } from "../lib/useTours";
import TourCard from "../components/TourCard";
import { Search } from "lucide-react";

const categories = ['All', 'Adventure', 'Nature', 'Beach', 'Historical', 'Devotional'];

export default function Tours() {
  const [category, setCategory] = useState('All');
  const [q, setQ] = useState('');
  const { tours } = useTours();

  const filteredTours = useMemo(() => {
    const qLower = q.toLowerCase();
    return tours.filter((t) => {
      const catMatch = category === 'All' || t.category === category;
      const qMatch = !qLower || (t.title + t.location + t.short).toLowerCase().includes(qLower);
      return catMatch && qMatch;
    });
  }, [category, q]);

  return (
    <main className="pt-24 sm:pt-32 pb-16 paper-texture min-h-screen">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-14">
          <div className="flex justify-center mb-3 sm:mb-4"><span className="leaf-divider">Curated journeys</span></div>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-6xl font-bold text-[#1F4D3A] leading-[1.1] mb-4 sm:mb-6">
            All our <em className="text-[#C87A50] not-italic">road stories</em>.
          </h1>
          <p className="text-base sm:text-lg text-[#4A3728] max-w-2xl mx-auto px-2">
            From adventure & nature to beaches, heritage and pilgrimages — filter by category to find your next getaway.
          </p>
        </div>

        {/* Category tabs + search */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6 mb-8 sm:mb-12">
          <div className="flex flex-wrap gap-1.5 sm:gap-2 justify-center md:justify-start">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors ${
                  category === c
                    ? 'bg-[#1F4D3A] text-white'
                    : 'bg-white text-[#4A3728] border border-[#E5DDCB] hover:border-[#1F4D3A]'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-72">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#6B8E5A]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search trips…"
              className="w-full pl-11 pr-4 py-2.5 sm:py-3 rounded-full bg-white border border-[#E5DDCB] text-sm text-[#2A2118] placeholder:text-[#8A7B65] focus:outline-none focus:border-[#1F4D3A] transition-colors"
            />
          </div>
        </div>

        {filteredTours.length === 0 ? (
          <div className="text-center py-24 text-[#5A4A38]">
            <p className="font-serif text-2xl mb-2">No trips match yet.</p>
            <p className="text-sm">Try a different filter or clear your search.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#1F4D3A]">
                {category === 'All' ? 'All trips' : `${category} trips`}
              </h2>
              <span className="text-xs text-[#6B8E5A] uppercase tracking-widest font-semibold">
                {filteredTours.length} {filteredTours.length === 1 ? 'trip' : 'trips'}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filteredTours.map((t) => <TourCard key={t.id} tour={t} />)}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
